import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ClipboardEvent, type DragEvent } from 'react';
import { toast } from 'sonner';
import { postBackendForm } from '@/utils/api-handler';
import type { AttachedFileMetadata } from '@/types/command-agent';

/** Local attachment row: staged file + its upload lifecycle. `uploaded` is set once the server has accepted it. */
export interface AttachmentItem {
    localId: string;
    file: File;
    status: 'pending' | 'uploading' | 'done' | 'error';
    uploaded?: AttachedFileMetadata;
}

const COMMAND_TEAM_FILES_UPLOAD_ENDPOINT = `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/command-team/files`;

export interface FileAttachmentQueueOptions {
    endpoint?: string;
    maxFileSizeBytes?: number;
    acceptedMimeTypes?: readonly string[];
    formFields?: Record<string, string>;
}

export const ACCEPT_FILES =
    '.txt,.pdf,.doc,.docx,.md,.csv,.html,.htm,.screen,.xlsx,.xls,.pptx,.ppt,text/html,text/markdown,image/*,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint';
const ACCEPT_FILE_RULES = ACCEPT_FILES.split(',')
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean);

function matchesAcceptRule(file: File, rule: string): boolean {
    if (rule.startsWith('.')) return file.name.toLowerCase().endsWith(rule);
    if (rule.endsWith('/*')) return file.type.toLowerCase().startsWith(rule.slice(0, -1));
    return file.type.toLowerCase() === rule;
}

/**
 * Same allow-list the file picker enforces through its `accept` attribute.
 * Drag-drop and paste bypass that attribute, so they are checked here instead.
 */
function isAcceptedFile(file: File): boolean {
    return ACCEPT_FILE_RULES.some((rule) => matchesAcceptRule(file, rule));
}

export const MAX_ATTACH_FILES = 5;
export const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB

function parseCommandTeamFileUploadResponse(res: unknown): AttachedFileMetadata | undefined {
    if (!res || typeof res !== 'object') return undefined;
    const coerce = (o: unknown): AttachedFileMetadata | undefined => {
        if (!o || typeof o !== 'object') return undefined;
        const x = o as Record<string, unknown>;
        const id = x.id ?? x.file_id;
        const name =
            typeof x.file_name === 'string' ? x.file_name : typeof x.fileName === 'string' ? (x.fileName as string) : undefined;
        if (id == null || name == null) return undefined;
        return {
            id: String(id),
            file_name: name,
            file_url: typeof x.file_url === 'string' ? x.file_url : typeof x.fileUrl === 'string' ? (x.fileUrl as string) : undefined,
            file_size: typeof x.file_size === 'number' ? x.file_size : typeof x.fileSize === 'number' ? (x.fileSize as number) : undefined,
            mime_type: typeof x.mime_type === 'string' ? x.mime_type : typeof x.mimeType === 'string' ? (x.mimeType as string) : undefined,
        };
    };
    const root = res as Record<string, unknown>;
    return (
        coerce(res) ??
        coerce(root.file) ??
        coerce(Array.isArray(root.files) ? root.files[0] : undefined) ??
        coerce(root.data)
    );
}

/**
 * Owns the staged attachment queue for a chat input: drag/drop/paste/select → pending,
 * an auto-upload effect drives pending → uploading → done (or drops the row on failure).
 * Returns stable handlers and a `clear()` for callers to drain after sending a message.
 */
export function useFileAttachmentQueue(options?: FileAttachmentQueueOptions) {
    const endpoint = options?.endpoint ?? COMMAND_TEAM_FILES_UPLOAD_ENDPOINT;
    const maxFileSizeBytes = options?.maxFileSizeBytes ?? MAX_FILE_SIZE_BYTES;
    const acceptedMimeTypes = options?.acceptedMimeTypes;
    const formFields = options?.formFields;
    const [attachedFiles, setAttachedFiles] = useState<AttachmentItem[]>([]);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    /** Prevents double-uploading the same localId (React Strict Mode, effect re-entry). Pruned when rows disappear. */
    const uploadKickoffRef = useRef<Set<string>>(new Set());

    const processFiles = useCallback((files: File[]) => {
        if (!files.length) return;

        const sizeLabel = `${Math.round(maxFileSizeBytes / (1024 * 1024))} MB`;
        const overSize: File[] = [];
        const wrongType: File[] = [];
        const empty: File[] = [];
        const validFiles: File[] = [];
        files.forEach((file) => {
            if (file.size > maxFileSizeBytes) overSize.push(file);
            else if (file.size === 0) empty.push(file);
            else if (
                acceptedMimeTypes
                    ? !acceptedMimeTypes.includes(file.type)
                    : !isAcceptedFile(file)
            )
                wrongType.push(file);
            else validFiles.push(file);
        });
        if (overSize.length > 0) {
            toast.error(
                overSize.length === 1
                    ? `"${overSize[0].name}" is over ${sizeLabel} and was not added.`
                    : `${overSize.length} file(s) over ${sizeLabel} were not added.`,
            );
        }
        if (empty.length > 0) {
            toast.error(
                empty.length === 1
                    ? `"${empty[0].name}" is empty and was not added.`
                    : `${empty.length} file(s) are empty and were not added.`,
            );
        }
        if (wrongType.length > 0) {
            toast.error(
                wrongType.length === 1
                    ? `"${wrongType[0].name}" is not a supported file type and was not added.`
                    : `${wrongType.length} file(s) are not a supported file type and were not added.`,
            );
        }

        setAttachedFiles((prev) => {
            const newItems = validFiles
                .slice(0, MAX_ATTACH_FILES - prev.length)
                .map((file) => ({
                    localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    file,
                    status: 'pending' as const,
                }));
            if (newItems.length === 0) return prev;
            return [...prev, ...newItems].slice(0, MAX_ATTACH_FILES);
        });
    }, [maxFileSizeBytes, acceptedMimeTypes]);

    useEffect(() => {
        const activeIds = new Set(attachedFiles.map((a) => a.localId));
        for (const id of [...uploadKickoffRef.current]) {
            if (!activeIds.has(id)) uploadKickoffRef.current.delete(id);
        }

        const toStart = attachedFiles.filter((item) => item.status === 'pending' && !uploadKickoffRef.current.has(item.localId));
        if (toStart.length === 0) return;

        for (const item of toStart) uploadKickoffRef.current.add(item.localId);

        const startIds = new Set(toStart.map((i) => i.localId));
        setAttachedFiles((prev) => prev.map((a) => (startIds.has(a.localId) ? { ...a, status: 'uploading' as const } : a)));

        for (const { localId, file } of toStart) {
            const formData = new FormData();
            formData.append('file', file);
            for (const [key, value] of Object.entries(formFields ?? {})) {
                formData.append(key, value);
            }

            postBackendForm(endpoint, formData)
                .then((res) => {
                    const meta = parseCommandTeamFileUploadResponse(res.data);
                    if (meta?.id) {
                        setAttachedFiles((prev) =>
                            prev.map((a) => (a.localId === localId ? { ...a, status: 'done' as const, uploaded: meta } : a)),
                        );
                    } else {
                        setAttachedFiles((prev) => prev.filter((a) => a.localId !== localId));
                        toast.error(`Failed to upload ${file.name}`);
                    }
                })
                .catch(() => {
                    setAttachedFiles((prev) => prev.filter((a) => a.localId !== localId));
                    toast.error(`Failed to upload ${file.name}`);
                })
                .finally(() => {
                    uploadKickoffRef.current.delete(localId);
                });
        }
    }, [attachedFiles, endpoint, formFields]);

    const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files?.length) return;
        processFiles(Array.from(files));
        e.target.value = '';
    }, [processFiles]);

    const handleDragOver = useCallback((e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes('Files')) e.dataTransfer.dropEffect = 'copy';
        setIsDraggingOver(true);
    }, []);

    const handleDragLeave = useCallback((e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback((e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);
        const files = e.dataTransfer.files;
        if (!files?.length) return;
        processFiles(Array.from(files));
    }, [processFiles]);

    const handlePaste = useCallback((e: ClipboardEvent) => {
        const files = e.clipboardData?.files;
        if (!files?.length) return;
        // Pastes from docs (Word, Google Docs, Notion) carry both a rendered image
        // and the underlying text — prefer the text and let the default paste run.
        const types = e.clipboardData?.types;
        const hasText =
            (types?.includes('text/plain') && e.clipboardData?.getData('text/plain')) ||
            (types?.includes('text/html') && e.clipboardData?.getData('text/html'));
        if (hasText) return;
        e.preventDefault();
        processFiles(Array.from(files));
    }, [processFiles]);

    const removeAttachedFile = useCallback((index: number) => {
        setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const clear = useCallback(() => setAttachedFiles([]), []);

    return {
        attachedFiles,
        isDraggingOver,
        fileInputRef,
        processFiles,
        handleFileSelect,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handlePaste,
        removeAttachedFile,
        clear,
    };
}
